import { IDropdownOption } from '@fluentui/react';
import { DateInput } from 'components/controls/DateInput';
import { cloneDeep } from 'lodash';
import Select from 'components/controls/Select';
import { ConditionalRender } from 'components/layout/ConditionalRender';
import FilterBar from 'components/layout/FilterBar';
import { ReadOn, usePermissions, UserPermission } from 'modules/permissions';
import { useCallback, useEffect, useState } from 'react';
import { ObservationConcernLevelEnum } from 'shared/enums/ObservationConcernLevel.enum';
import { ObservationStatusEnum } from 'shared/enums/ObservationStatus.enum';
import { mapToDropdownOptions } from 'shared/utils/mapping.utils';
import { Issue, IssueListQuery } from '../../../queries/issues-list.queries.types';
import {
   DepotLookup,
   DivisionLookup,
   RegionLookup,
   SHEQManagerLookup,
   EnumLookup,
   FilterLookup as IssueFilterLookup
} from '../types/issues-filter.types';
import { isEmpty, isNullOrEmpty, isNullOrUndefined } from 'shared/utils/null.utils';
import { getConcernLevelNumericValue } from 'shared/utils/concern-level.utils';
import { FilterSelectV2 } from 'components/controls/Select';
import { getCookie, setCookie, setDateFromCookie } from 'shared/utils/cookies.utils';
import { ObservationListFilterEnum } from 'shared/enums/ObservationListFilter.enum';
import { useTranslation } from 'react-i18next';

interface IssuesFilterBarProps {
   filterDataLoaded: boolean;
   showFilterBar: boolean;
   toggleFilterBar: () => void;
   filterRequest: IssueListQuery;
   issueData: Issue[] | undefined;
   setFilterRequest: (newFilter: IssueListQuery) => void;
   resetFilter: () => void;
}

const defaultLookupValues: IssueFilterLookup = {
   concernLevels: [],
   statuses: [],
   depots: [],
   divisions: [],
   regions: [],
   sHEQManagers: []
};

export const IssuesFilterBar = ({
    filterDataLoaded,
    showFilterBar,
    toggleFilterBar,
    filterRequest,
    issueData: data,
    setFilterRequest,
    resetFilter
}: IssuesFilterBarProps) => {
    const [lookupValues, setLookUpValues] = useState<IssueFilterLookup | undefined>();
    const [originalLookupValues, setOriginalLookUpValues] = useState<IssueFilterLookup | undefined>();

    const { hasPermission } = usePermissions();

    useEffect(() => {
        if (!data) {
            return;
        }

        let dateFrom = setDateFromCookie(getCookie(ObservationListFilterEnum.IssueDateFrom));
        let dateTo = setDateFromCookie(getCookie(ObservationListFilterEnum.IssueDateTo));
        let concernLevel =
            (getCookie(ObservationListFilterEnum.IssueConcernLevel)?.split(',') as ObservationConcernLevelEnum[]) ?? [];
        let observationStatus = (getCookie(ObservationListFilterEnum.IssueStatus)?.split(
            ','
        ) as ObservationStatusEnum[]) ?? [ObservationStatusEnum.Open, ObservationStatusEnum.Closed];
        let regionId = getCookie(ObservationListFilterEnum.IssueRegionIds)?.split(',').map(Number) ?? [];
        let divisionId = getCookie(ObservationListFilterEnum.IssueDivisionIds)?.split(',').map(Number) ?? [];
        let depotPcId = getCookie(ObservationListFilterEnum.IssueDepotPcId)?.split(',').map(Number) ?? [];
        let sHEQManagerId = getCookie(ObservationListFilterEnum.IssueManagerIds)?.split(',').map(Number) ?? [];

        setFilterRequest({
            ...filterRequest,
            dateFrom: dateFrom,
            dateTo: dateTo,
            concernLevel: concernLevel,
            observationStatus: observationStatus,
            regionId: regionId,
            divisionId: divisionId,
            depotPcId: depotPcId,
            sHEQManagerId: sHEQManagerId
        });

        if (
            !isNullOrEmpty(dateFrom) ||
            !isNullOrEmpty(dateTo) ||
            concernLevel.length > 0 ||
            (observationStatus.toString() != 'OPEN,CLOSED' && observationStatus.toString() != 'CLOSED,OPEN') ||
            regionId.length > 0 ||
            divisionId.length > 0 ||
            depotPcId.length > 0 ||
            sHEQManagerId.length > 0
        ) {
            toggleFilterBar();
        }
        const newLookupValues = cloneDeep<IssueFilterLookup>(lookupValues ?? defaultLookupValues);

        newLookupValues.concernLevels = data
            .map(x => {
                return {
                    id: x.concernLevel as ObservationConcernLevelEnum,
                    name: x.concernLevel.toProperCase()
                };
            })
            .distinct(x => x.name)
            .sort(
                (
                    a: EnumLookup<ObservationConcernLevelEnum>,
                    b: EnumLookup<ObservationConcernLevelEnum>
                ) => {
                    const sortValueA = getConcernLevelNumericValue(a.id);
                    const sortValueB = getConcernLevelNumericValue(b.id);

                    return (false ? sortValueA < sortValueB : sortValueA > sortValueB) ? 1 : -1;
                }
            );

        newLookupValues.statuses = data
            .map(x => {
                return {
                    id: x.observationStatus as ObservationStatusEnum,
                    name: x.observationStatus.toProperCase()
                };
            })
            .distinct(x => x.id);

        if (
            !newLookupValues?.statuses.any(s => s.id === ObservationStatusEnum.Archived) &&
            hasPermission(ReadOn(UserPermission.Archive))
        ) {
            newLookupValues.statuses.push({
                id: ObservationStatusEnum.Archived,
                name: ObservationStatusEnum.Archived.toProperCase()
            });
        }

        newLookupValues.depots = data
            .filter(x => x.depotPcName != null)
            .map(x => {
                return {
                    regionId: x.regionId,
                    divisionId: x.divisionId,
                    pcId: x.depotPcId,
                    pc: x.depotPC,
                    pcName: x.depotPcName
                };
            })
            .distinct(x => x.pcName)
            .sort((a: DepotLookup, b: DepotLookup) => {
                const sortValueA = a.pc;
                const sortValueB = b.pc;

                return (false ? sortValueA < sortValueB : sortValueA > sortValueB) ? 1 : -1;
            });

        newLookupValues.divisions = data
            .filter(x => x.divisionName != null)
            .map(x => {
                return {
                    regionId: x.regionId,
                    divisionId: x.divisionId,
                    divisionName: x.divisionName
                };
            })
            .distinct(x => x.divisionId)
            .sort((a: DivisionLookup, b: DivisionLookup) => {
                const sortValueA = a.divisionName;
                const sortValueB = b.divisionName;

                return (false ? sortValueA < sortValueB : sortValueA > sortValueB) ? 1 : -1;
            });

        newLookupValues.regions = data
            .filter(x => x.regionName != null)
            .map(x => {
                return {
                    regionId: x.regionId,
                    regionName: x.regionName
                };
            })
            .distinct(x => x.regionId)
            .sort((a: RegionLookup, b: RegionLookup) => {
                const sortValueA = a.regionName;
                const sortValueB = b.regionName;

                return (false ? sortValueA < sortValueB : sortValueA > sortValueB) ? 1 : -1;
            });

        newLookupValues.sHEQManagers = data
            .filter(x => x.sHEQManager != null)
            .map(x => {
                return {
                    sHEQManagerId: x.sHEQManagerId,
                    sHEQManager: x.sHEQManager
                };
            })
            .distinct(x => x.sHEQManagerId)
            .sort((a: SHEQManagerLookup, b: SHEQManagerLookup) => {
                const sortValueA = a.sHEQManager;
                const sortValueB = b.sHEQManager;

                return (false ? sortValueA < sortValueB : sortValueA > sortValueB) ? 1 : -1;
            });

        setLookUpValues(newLookupValues);
        setOriginalLookUpValues(newLookupValues);
    }, [filterDataLoaded]);

    const onResetFilter = () => {
        const newLookupValues = cloneDeep<IssueFilterLookup>(lookupValues ?? defaultLookupValues);
        newLookupValues.divisions = originalLookupValues?.divisions;
        newLookupValues.depots = originalLookupValues?.depots;
        setLookUpValues(newLookupValues);

        resetCookies();
        resetFilter();
    };

    const onDateFromSelect = (date: Date | null | undefined) => {
        setCookie(ObservationListFilterEnum.IssueDateFrom, date);

        setFilterRequest({
            ...filterRequest,
            dateFrom: date ?? undefined
        });
    };

    const onDateToSelect = (date: Date | null | undefined) => {
        if (!isNullOrUndefined(date)) {
            let toDate = new Date(date.setHours(23, 59, 59, 999));

            setCookie(ObservationListFilterEnum.IssueDateTo, toDate);

            setFilterRequest({
                ...filterRequest,
                dateTo: toDate ?? undefined
            });
        }
    };

    const onDepotSelect = (options?: IDropdownOption[]) => {
        let depotPcId = options?.map(x => Number(x.key));

        setCookie(ObservationListFilterEnum.IssueDepotPcId, depotPcId);

        setFilterRequest({
            ...filterRequest,
            depotPcId: depotPcId
        });
    };

    const onDivisionSelect = (options?: IDropdownOption[]) => {
        let divisionIds = options?.map(x => Number(x.key));

        setCookie(ObservationListFilterEnum.IssueDivisionIds, divisionIds);
        setCookie(ObservationListFilterEnum.IssueDepotPcId, '');

        setFilterRequest({
            ...filterRequest,
            divisionId: divisionIds,
            depotPcId: []
        });

        let depots =
            originalLookupValues?.depots?.filter(x => divisionIds?.any(r => r === x.divisionId))?.distinct(x => x.pcId) ??
            [];

        const newLookupValues = cloneDeep<IssueFilterLookup>(lookupValues ?? defaultLookupValues);
        newLookupValues.depots = depots;
        setLookUpValues(newLookupValues);
    };

    const onRegionSelect = (options?: IDropdownOption[]) => {
        let regionIds = options?.map(x => Number(x.key)) ?? [];

        setCookie(ObservationListFilterEnum.IssueRegionIds, regionIds);
        setCookie(ObservationListFilterEnum.IssueDivisionIds, '');
        setCookie(ObservationListFilterEnum.IssueDepotPcId, '');

        setFilterRequest({
            ...filterRequest,
            regionId: regionIds,
            divisionId: [],
            depotPcId: []
        });

        let divisions: DivisionLookup[] | undefined = [];
        let depots: DepotLookup[] | undefined = [];
        if (isEmpty(regionIds)) {
            divisions = originalLookupValues?.divisions ?? [];
            depots = originalLookupValues?.depots ?? [];
        } else {
            divisions =
                originalLookupValues?.divisions
                    ?.filter(x => regionIds?.any(r => r === x.regionId))
                    ?.distinct(x => x.divisionId) ?? [];

            depots =
                originalLookupValues?.depots?.filter(x => regionIds?.any(r => r === x.regionId))?.distinct(x => x.pcId) ??
                [];
        }

        const newLookupValues = cloneDeep<IssueFilterLookup>(lookupValues ?? defaultLookupValues);
        newLookupValues.divisions = divisions;
        newLookupValues.depots = depots;
        setLookUpValues(newLookupValues);
    };

    const onSHEQManagerSelect = (options?: IDropdownOption[]) => {
        let managerIds = options?.map(x => Number(x.key));

        setCookie(ObservationListFilterEnum.IssueManagerIds, managerIds);

        setFilterRequest({
            ...filterRequest,
            sHEQManagerId: managerIds
        });
    };

    const onConcernLevelSelect = (event: React.FormEvent<HTMLDivElement> | undefined, option?: IDropdownOption) => {
        if (option) {
            let concernLevels = filterRequest.concernLevel;

            const alreadySelected = concernLevels?.any(x => x === (option!.key as ObservationConcernLevelEnum));

            if (option.selected && !alreadySelected) {
                concernLevels!.push(option!.key as ObservationConcernLevelEnum);
            }

            if (!option.selected && alreadySelected) {
                concernLevels = concernLevels?.filter(x => x !== (option!.key as ObservationConcernLevelEnum));
            }

            setCookie(ObservationListFilterEnum.IssueConcernLevel, concernLevels);

            setFilterRequest({
                ...filterRequest,
                concernLevel: concernLevels
            });
        }
    };

    const onStatusSelect = (event: React.FormEvent<HTMLDivElement> | undefined, option?: IDropdownOption) => {
        if (option) {
            let selectedStatuses = filterRequest.observationStatus;

            const alreadySelected = selectedStatuses?.any(x => x === (option!.key as ObservationStatusEnum));

            if (option.selected && !alreadySelected) {
                selectedStatuses!.push(option!.key as ObservationStatusEnum);
            }

            if (!option.selected && alreadySelected) {
                selectedStatuses = selectedStatuses?.filter(x => x !== (option!.key as ObservationStatusEnum));
            }

            setCookie(ObservationListFilterEnum.IssueStatus, selectedStatuses);

            setFilterRequest({
                ...filterRequest,
                observationStatus: selectedStatuses
            });
        }
    };

    const resetCookies = () => {
        setCookie(ObservationListFilterEnum.IssueDateFrom, '');
        setCookie(ObservationListFilterEnum.IssueDateTo, '');
        setCookie(ObservationListFilterEnum.IssueConcernLevel, '');
        setCookie(ObservationListFilterEnum.IssueCategoryId, '');
        setCookie(ObservationListFilterEnum.IssueStatus, 'OPEN,CLOSED');
        setCookie(ObservationListFilterEnum.IssueRegionIds, '');
        setCookie(ObservationListFilterEnum.IssueDivisionIds, '');
        setCookie(ObservationListFilterEnum.IssueDepotPcId, '');
        setCookie(ObservationListFilterEnum.IssueManagerIds, '');
    };

    const showSHEQTeamFilter = useCallback(() => {
        return hasPermission(ReadOn(UserPermission.DepotLinks));
    }, [hasPermission]);

    return (
        <ConditionalRender condition={showFilterBar}>
            <FilterBar showReset onReset={onResetFilter}>
                <DateInput
                    placeholder={'Date From'}
                    borderless
                    restrictWidth
                    onDateSelect={onDateFromSelect}
                    value={filterRequest.dateFrom?.toDateString()}
                    transparent
                    useEnglish
                />
                <DateInput
                    placeholder={'DateTo'}
                    borderless
                    restrictWidth
                    onDateSelect={onDateToSelect}
                    value={filterRequest.dateTo?.toDateString()}
                    transparent
                    useEnglish
                />
                <Select
                    style={{ minWidth: '150px', marginTop: '-8px', marginLeft: '90px' }}
                    options={mapToDropdownOptions<EnumLookup<ObservationConcernLevelEnum>, IDropdownOption>(
                        lookupValues?.concernLevels,
                        level => level.id.toUpperCase(),
                        level => level.name
                    )}
                    transparent
                    multiSelect
                    placeholder={'Concern Level'}
                    selectedKeys={filterRequest.concernLevel}
                    onChange={onConcernLevelSelect}
                    calloutWidth='auto'
                />

                <Select
                    style={{ minWidth: '140px', marginTop: '-8px', marginLeft: '0px' }}
                    options={mapToDropdownOptions<EnumLookup<ObservationStatusEnum>, IDropdownOption>(
                        lookupValues?.statuses,
                        status => status.id.toUpperCase(),
                        status => status.name.toProperCase()
                    )}
                    transparent
                    multiSelect
                    placeholder={'Status'}
                    selectedKeys={filterRequest.observationStatus ?? []}
                    onChange={onStatusSelect}
                    calloutWidth='auto'
                />
            </FilterBar>
            {
                <ConditionalRender condition={showSHEQTeamFilter}>
                    <FilterBar>
                        <FilterSelectV2
                            style={{ minWidth: '120px', marginTop: '-2px', marginLeft: '10px' }}
                            options={mapToDropdownOptions<RegionLookup, IDropdownOption>(
                                lookupValues?.regions,
                                region => region.regionId,
                                region => `${region.regionName}`
                            )}
                            transparent
                            multiSelect
                            placeholder={'Region'}
                            selectedKeys={filterRequest.regionId}
                            onChangeMulti={onRegionSelect}
                        />

                        <FilterSelectV2
                            style={{ minWidth: '160px', marginTop: '-2px' }}
                            options={mapToDropdownOptions<DivisionLookup, IDropdownOption>(
                                lookupValues?.divisions,
                                division => division.divisionId,
                                division => `${division.divisionName}`
                            )}
                            transparent
                            multiSelect
                            placeholder={'Division'}
                            selectedKeys={filterRequest.divisionId}
                            onChangeMulti={onDivisionSelect}
                        />

                        <FilterSelectV2
                            style={{ minWidth: '300px', marginTop: '-2px' }}
                            options={mapToDropdownOptions<DepotLookup, IDropdownOption>(
                                lookupValues?.depots,
                                depot => depot.pcId,
                                depot => `${depot.pcName}`
                            )}
                            transparent
                            multiSelect
                            placeholder={'Depot'}
                            selectedKeys={filterRequest.depotPcId}
                            onChangeMulti={onDepotSelect}
                        />

                        <FilterSelectV2
                            style={{ minWidth: '100px', marginTop: '-2px' }}
                            options={mapToDropdownOptions<SHEQManagerLookup, IDropdownOption>(
                                lookupValues?.sHEQManagers,
                                sheqManager => sheqManager.sHEQManagerId,
                                sheqManager => `${sheqManager.sHEQManager}`
                            )}
                            transparent
                            multiSelect
                            placeholder={'SHEQ Manager'}
                            selectedKeys={filterRequest.sHEQManagerId}
                            onChangeMulti={onSHEQManagerSelect}
                        />
                    </FilterBar>
                </ConditionalRender>
            }
      </ConditionalRender>
   );
};
